/**
 * Putting the outline upright before anything is measured.
 *
 * Only roll is corrected, and only roll can be: rotating the image plane loses nothing, whereas yaw and pitch throw away parts of the face that a 2D rotation cannot bring back. Those two are gated instead of corrected, which is why the features are called scale-free under near-frontal capture rather than simply scale-free.
 *
 * The midline is fitted through all seventeen mirror-pair midpoints and both poles rather than taken from the two poles alone. A real face is not symmetric, and a midline resting on two landmarks inherits the noise of exactly those two.
 */
import { MENTON_INDEX, MIRROR_PAIRS, SUPERIOR_INDEX, type Outline, type Point } from './oval';

export interface Normalised {
  /** The outline rotated so the fitted midline is vertical, still in pixels. */
  outline: Outline;
  /** How far the outline was rotated, degrees, positive clockwise in image coordinates. */
  rollAppliedDeg: number;
  /** The fitted midline's direction before rotation, as a unit vector. */
  axis: Point;
  /**
   * Where the face's midline sits horizontally once the outline is upright.
   *
   * Taken from the two poles, which are the only ring landmarks anatomically on the midline, and deliberately not from the pair midpoints. Two earlier choices both destroyed the symmetry measurement: taking each chord's own midpoint makes the halves equal by construction, and fitting the midline through the pair midpoints lets it slide sideways until it absorbs most of the very asymmetry the gate exists to catch. The pair midpoints still set the *direction*, which is what they are good for.
   */
  midX: number;
}

const mean = (xs: readonly number[]): number => xs.reduce((s, x) => s + x, 0) / xs.length;

/**
 * The face's own vertical axis, from the poles and every pair midpoint.
 *
 * Total least squares rather than a y-on-x fit: the axis is close to vertical, where an ordinary regression of x on y is fine but the reverse is not, and a principal axis is the one that does not care which way round the problem is posed.
 */
export function midlineAxis(outline: Outline): Point {
  const centres = midpoints(outline);

  const cx = mean(centres.map((p) => p.x));
  const cy = mean(centres.map((p) => p.y));
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  for (const p of centres) {
    const dx = p.x - cx;
    const dy = p.y - cy;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  }

  // The principal direction of the midpoint cloud; the eigenvector of the larger eigenvalue.
  const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  let ax = Math.cos(theta);
  let ay = Math.sin(theta);
  // Point it from the superior pole towards the menton, so the sign is the face's own, not the solver's.
  const down = {
    x: outline[MENTON_INDEX]!.x - outline[SUPERIOR_INDEX]!.x,
    y: outline[MENTON_INDEX]!.y - outline[SUPERIOR_INDEX]!.y,
  };
  if (ax * down.x + ay * down.y < 0) {
    ax = -ax;
    ay = -ay;
  }
  return { x: ax, y: ay };
}

/** The midpoints the midline is fitted through: both poles and every mirror pair. */
function midpoints(outline: Outline): Point[] {
  const out: Point[] = [outline[SUPERIOR_INDEX]!, outline[MENTON_INDEX]!];
  for (const [a, b] of MIRROR_PAIRS) {
    const p = outline[a]!;
    const q = outline[b]!;
    out.push({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 });
  }
  return out;
}

/** Rotates the outline so its own midline runs straight down the image. */
export function normalise(outline: Outline): Normalised {
  const axis = midlineAxis(outline);
  // The angle that takes the axis onto (0, 1).
  const angle = Math.atan2(axis.x, axis.y);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const pivot = outline[SUPERIOR_INDEX]!;

  const rotated = outline.map((p) => {
    const dx = p.x - pivot.x;
    const dy = p.y - pivot.y;
    return { x: pivot.x + dx * cos - dy * sin, y: pivot.y + dx * sin + dy * cos };
  });

  const midX = (rotated[SUPERIOR_INDEX]!.x + rotated[MENTON_INDEX]!.x) / 2;

  return { outline: rotated, rollAppliedDeg: (angle * 180) / Math.PI, axis, midX };
}
